import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { TableColumnOptions, TableColumns } from '../../types/table-columns';
import { ResourceType } from '../../types/resource-type';

@Component({
  selector: 'ml-data-table',
  template: `
    <mat-card fxLayout="column" style="max-height: 100%">
      <div class="container full-height" fxLayout="column" fxFlexFill>
        <ml-table-header
          [title]="title"
          [titleIcon]="titleIcon"
          [canAdd]="canAdd"
          (add)="add.emit()"
        >
          <ng-content select="[rightHeader]"></ng-content>
        </ml-table-header>
        <mat-form-field
          *ngIf="enableFiltering && dataSource && dataSource.data.length !== 0"
          class="full-width"
        >
          <mat-label>{{ filterLabel | translate }}</mat-label>
          <input matInput (keyup)="applyFilter($event.target.value)" />
        </mat-form-field>

        <div
          [ngStyle]="{
            'max-height': tableHeight ? tableHeight + 'px' : '100%'
          }"
          style="overflow: auto; position: relative"
          fxLayout="row"
        >
          <table mat-table [dataSource]="dataSource" matSort class="full-width">
            <ng-container *ngIf="showIndex" matColumnDef="index">
              <th mat-header-cell *matHeaderCellDef mat-sort-header>#</th>
              <td mat-cell *matCellDef="let element; let i = index">
                {{ i + 1 }}
              </td>
            </ng-container>

            <ng-container
              *ngFor="let column of (tableColumns | keyvalue)"
              matColumnDef="{{ column.key }}"
            >
              <ng-container *ngIf="column.key === 'actions'">
                <th mat-header-cell *matHeaderCellDef></th>
                <td mat-cell *matCellDef="let element" class="action-column">
                  <ml-comments-icon
                    *ngIf="canComment"
                    (click)="$event.stopPropagation()"
                    [resource]="resourceType"
                    [resourceId]="element?.id"
                    [commented]="element.commented"
                    [title]="
                      element?.firstName
                        ? element?.firstName + element?.lastName
                        : element?.name
                    "
                  ></ml-comments-icon>
                  <button
                    *ngIf="canEdit || canRemove"
                    mat-icon-button
                    [matMenuTriggerFor]="menu"
                    (click)="
                      $event.stopPropagation(); activeElement.emit(element)
                    "
                  >
                    <mat-icon svgIcon="more_vert"></mat-icon>
                  </button>
                  <mat-menu #menu="matMenu">
                    <ng-content select="[mlTableOptions]"></ng-content>
                    <button
                      *ngIf="canEdit"
                      mat-menu-item
                      (click)="edit.emit(element)"
                    >
                      <mat-icon matPrefix svgIcon="edit"></mat-icon>
                      {{ 'edit' | translate | titlecase }}
                    </button>
                    <button
                      *ngIf="canRemove"
                      mat-menu-item
                      (click)="remove.emit(element)"
                    >
                      <mat-icon
                        matPrefix
                        svgIcon="delete"
                        color="warn"
                      ></mat-icon>
                      {{ 'remove' | translate | titlecase }}
                    </button>
                  </mat-menu>
                </td>
              </ng-container>
              <ng-container *ngIf="column.key !== 'actions'">
                <th mat-header-cell *matHeaderCellDef>
                  {{ column.value.title | translate | titlecase }}
                </th>
                <td mat-cell *matCellDef="let element">
                  <div *ngIf="!column.value.isArray; else isArray">
                    <div *ngIf="column.value.displayFn; else noProperty">
                      {{
                        column.value.translate
                          ? (column.value.displayFn(element) | translate)
                          : column.value.displayFn(element)
                      }}
                    </div>
                    <ng-template #noProperty>
                      {{
                        element[column.key]
                          ? column.value.translate
                            ? (element[column.key] | translate)
                            : element[column.key]
                          : '-'
                      }}</ng-template
                    >
                  </div>
                  <ng-template #isArray>
                    <div
                      *ngFor="
                        let item of element[
                          getArrayKey(column.key, column.value)
                        ]
                      "
                    >
                      <div *ngIf="column.value.displayFn; else noPropertyArray">
                        {{ item ? column.value.displayFn(item) : '-' }}
                      </div>
                      <ng-template #noPropertyArray>
                        {{ item ? item : '-' }}</ng-template
                      >
                    </div>
                    <div
                      *ngIf="
                        element[getArrayKey(column.key, column.value)] &&
                        element[getArrayKey(column.key, column.value)]
                          .length === 0
                      "
                    >
                      -
                    </div>
                  </ng-template>
                </td>
              </ng-container>
            </ng-container>

            <tr
              mat-header-row
              *matHeaderRowDef="displayedColumns; sticky: true"
            ></tr>
            <tr
              [ngClass]="{ selectable: selectable }"
              (click)="onSelect(row)"
              mat-row
              *matRowDef="let row; columns: displayedColumns"
            ></tr>
          </table>
        </div>
        <div
          [hidden]="
            !(
              dataSource &&
              enablePagination &&
              !loading &&
              dataSource.filteredData.length !== 0
            )
          "
        >
          <mat-paginator
            [pageSizeOptions]="[25, 50, 100, 500]"
            [pageSize]="100"
          ></mat-paginator>
        </div>
        <div
          *ngIf="
            (!dataSource ||
              dataSource.filteredData.length === 0 ||
              dataSource.data.length === 0) &&
            !loading
          "
        >
          <h4 class="text-center">
            {{ 'there-are-no' | translate }} {{ title | translate | lowercase }}
          </h4>
        </div>
        <div *ngIf="loading">
          <mat-spinner color="primary" style="margin: auto"></mat-spinner>
        </div>
      </div>
    </mat-card>
  `,
  styleUrls: ['./data-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DataTableComponent implements OnInit {
  @Input() title: string;
  @Input() titleIcon: string;
  @Input() loading: boolean;
  @Input() enablePagination = true;
  @Input() enableFiltering = false;
  @Input() tableColumns: TableColumns;
  @Input() canAdd = true;
  @Input() tableHeight: number;
  @Input() canEdit = true;
  @Input() canRemove = true;
  @Input() canComment: boolean;
  @Input() resourceType: ResourceType;
  @Input() selectable = false;
  @Input() filterLabel = 'Filter';
  @Input() showIndex = false;

  @Input() set data(data: any[]) {
    if (data) {
      this.dataSource = new MatTableDataSource(data);
      this.dataSource.sort = this.sort;
      if (this.enablePagination) {
        this.dataSource.paginator = this.paginator;
      }
    }
  }

  displayedColumns: string[];
  dataSource: MatTableDataSource<any>;

  /**
   * Used to emit the row in which the user clicks the 3 dots
   */
  @Output() activeElement = new EventEmitter();
  @Output() edit: EventEmitter<any> = new EventEmitter();
  @Output() remove: EventEmitter<any> = new EventEmitter();
  @Output() add: EventEmitter<void> = new EventEmitter();
  @Output() selectElement: EventEmitter<any> = new EventEmitter();

  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  constructor() {}

  ngOnInit() {
    this.displayedColumns = Object.keys(this.tableColumns);
    if (this.showIndex) {
      this.displayedColumns = ['index', ...this.displayedColumns];
    }
  }

  getArrayKey(column: string, columnOptions: TableColumnOptions): string {
    return columnOptions.arrayColumn ? columnOptions.arrayColumn : column;
  }

  applyFilter(filterValue: string) {
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  onSelect(row) {
    if (this.selectable) {
      this.selectElement.emit(row);
    }
  }
}
